import type {
  GleenAttachmentAddedData,
  GleenMessageSubmittedData,
  GleenProductDisplayedData,
  GleenProductPageOpenedData,
  GleenQuizOptionsRenderedData,
} from '@integration-layer/types/gleen-widget'

/** Collect window from the first card; Alhena loads cards async with large gaps. */
const PRODUCT_DISPLAY_BATCH_MS = 3000
const PRODUCT_CLICK_DEDUP_MS = 500
const PRODUCT_CARD_SELECTOR = '.alhena-product-card'

let trackingInitialized = false
let productCardClickHandler: ((event: MouseEvent) => void) | null = null

export const resetAlhenaChatTracking = () => {
  trackingInitialized = false

  if (productCardClickHandler) {
    document.removeEventListener('click', productCardClickHandler)
    productCardClickHandler = null
  }
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    resetAlhenaChatTracking()
  })
}

const getAttachmentType = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType === 'application/pdf') return 'pdf'
  return mimeType.split('/')[0] || mimeType
}

/** Alhena SDK uses `masterProductId`; docs still document `productId`. */
const getGleenProductId = (
  payload?: Pick<GleenProductDisplayedData, 'masterProductId' | 'productId'>
): string | undefined => payload?.masterProductId || payload?.productId

type AlhenaProductCardData = {
  external_product_id?: string
  external_variant_id?: string
}

const getProductIdFromCard = (card: Element): string | undefined => {
  const raw = card.getAttribute('data-productdata')
  if (raw) {
    try {
      const data = JSON.parse(raw) as AlhenaProductCardData
      if (data.external_product_id) return data.external_product_id
      if (data.external_variant_id) return data.external_variant_id
    } catch {
      // ignore invalid JSON from the widget
    }
  }

  return card.getAttribute('data-variantid') || undefined
}

const mapInputMethod = (
  submitAction: GleenMessageSubmittedData['submitAction']
): 'button' | 'enter_key' | null => {
  if (submitAction === 'button_click') return 'button'
  if (submitAction === 'enter_key') return 'enter_key'
  return null
}

const resetConversationState = (state: {
  hasStartedChat: boolean
  lastCategoriesList: string
  selectedCategory: string
  lastSuggestedProductIds: string
  pendingProductIds: Set<string>
  productDisplayTimer: ReturnType<typeof setTimeout> | null
}) => {
  state.hasStartedChat = false
  state.lastCategoriesList = '-'
  state.selectedCategory = '-'
  state.lastSuggestedProductIds = '-'

  if (state.productDisplayTimer) {
    clearTimeout(state.productDisplayTimer)
    state.productDisplayTimer = null
  }

  state.pendingProductIds.clear()
}

/**
 * Subscribes to Alhena SDK events and forwards them to GA dataLayer pushes.
 * Must be called once after the Gleen widget script is loaded.
 */
export function useAlhenaChatTracking() {
  const setupTracking = () => {
    if (!import.meta.client || trackingInitialized) return false

    const gleenWidget = window.gleenWidget
    if (!gleenWidget?.on) return false

    trackingInitialized = true

    const {
      dispatchOpenChatBubble,
      dispatchCloseChat,
      dispatchStartChat,
      dispatchQueryValue,
      dispatchAddAttachment,
      dispatchSendMessage,
      dispatchSuggestedCategories,
      dispatchSuggestedCategoryClick,
      dispatchSuggestedProducts,
      dispatchSuggestedProductClick,
      formatCategoryList,
      formatCategoryLabel,
    } = useGAAlhenaChatEvents()

    const state = {
      hasStartedChat: false,
      lastCategoriesList: '-',
      selectedCategory: '-',
      lastSuggestedProductIds: '-',
      pendingProductIds: new Set<string>(),
      productDisplayTimer: null as ReturnType<typeof setTimeout> | null,
      lastProductClickId: '',
      lastProductClickAt: 0,
    }

    const flushSuggestedProducts = () => {
      if (state.pendingProductIds.size === 0) return

      const suggestedProductIds = [...state.pendingProductIds].join('|')
      state.lastSuggestedProductIds = suggestedProductIds
      dispatchSuggestedProducts(state.selectedCategory, suggestedProductIds)
      state.pendingProductIds.clear()
      state.productDisplayTimer = null
    }

    const queueSuggestedProduct = (productId: string) => {
      // Alhena fires `product:displayed` per card as each loads async, often
      // with >1s gaps. Collect from the first card for a fixed window so C009
      // gets one pipe-joined list (like suggested_categories).
      const isNewBatch = state.pendingProductIds.size === 0
      state.pendingProductIds.add(productId)

      if (!isNewBatch) return

      state.productDisplayTimer = setTimeout(
        flushSuggestedProducts,
        PRODUCT_DISPLAY_BATCH_MS
      )
    }

    /**
     * Some Alhena card templates (e.g. "Maggiori dettagli") navigate via a plain
     * `<a href>` and never emit `product:page_opened`. DOM fallback covers that.
     * Dedupes against the SDK event when both fire.
     */
    const trackSuggestedProductClick = (productId: string) => {
      const now = Date.now()
      if (
        productId === state.lastProductClickId &&
        now - state.lastProductClickAt < PRODUCT_CLICK_DEDUP_MS
      ) {
        return
      }

      state.lastProductClickId = productId
      state.lastProductClickAt = now

      dispatchSuggestedProductClick(
        state.selectedCategory,
        state.lastSuggestedProductIds,
        productId
      )
    }

    productCardClickHandler = (event: MouseEvent) => {
      // add_to_cart handlers call preventDefault; skip those
      if (event.defaultPrevented) return

      const target = event.target
      if (!(target instanceof Element)) return

      const card = target.closest(PRODUCT_CARD_SELECTOR)
      if (!card) return

      const anchor = target.closest('a[href]')
      if (!anchor || !card.contains(anchor)) return

      const productId = getProductIdFromCard(card)
      if (!productId) return

      trackSuggestedProductClick(productId)
    }

    document.addEventListener('click', productCardClickHandler)

    gleenWidget.on('widget:opened', () => {
      dispatchOpenChatBubble()
    })

    gleenWidget.on('widget:closed', () => {
      dispatchCloseChat()
      resetConversationState(state)
    })

    gleenWidget.on('ticket:closed', () => {
      resetConversationState(state)
    })

    gleenWidget.on('ticket:input_focused', () => {
      if (state.hasStartedChat) return
      state.hasStartedChat = true
      dispatchStartChat()
    })

    gleenWidget.on('ticket:message_submitted', (data?: unknown) => {
      const payload = data as GleenMessageSubmittedData | undefined
      if (!payload?.text) return

      if (payload.submitAction === 'quiz_option_clicked') {
        const categoryClicked = formatCategoryLabel(payload.text)
        dispatchSuggestedCategoryClick(
          state.lastCategoriesList,
          categoryClicked
        )
        state.selectedCategory = categoryClicked
        return
      }

      const inputMethod = mapInputMethod(payload.submitAction)
      if (!inputMethod) return

      dispatchQueryValue(payload.text)
      dispatchSendMessage(payload.text, inputMethod)
    })

    gleenWidget.on('ticket:attachment_added', (data?: unknown) => {
      const payload = data as GleenAttachmentAddedData | undefined
      if (!payload) return

      dispatchAddAttachment(
        getAttachmentType(payload.fileType ?? ''),
        payload.fileName ?? '-'
      )
    })

    gleenWidget.on('ticket:quiz_options_rendered', (data?: unknown) => {
      const payload = data as GleenQuizOptionsRenderedData | undefined
      if (!payload?.options?.length) return

      const categoriesList = formatCategoryList(payload.options)
      state.lastCategoriesList = categoriesList
      dispatchSuggestedCategories(categoriesList)
    })

    gleenWidget.on('product:displayed', (data?: unknown) => {
      const payload = data as GleenProductDisplayedData | undefined
      const productId = getGleenProductId(payload)
      if (!productId) return

      queueSuggestedProduct(productId)
    })

    gleenWidget.on('product:page_opened', (data?: unknown) => {
      const payload = data as GleenProductPageOpenedData | undefined
      const productId = getGleenProductId(payload)
      if (!productId) return

      trackSuggestedProductClick(productId)
    })

    return true
  }

  const waitForWidgetAndSetupTracking = () => {
    if (!import.meta.client || trackingInitialized) return

    if (setupTracking() || trackingInitialized) return

    const intervalId = window.setInterval(() => {
      if (trackingInitialized) {
        window.clearInterval(intervalId)
      } else {
        setupTracking()
      }
    }, 250)

    window.setTimeout(() => window.clearInterval(intervalId), 30000)
  }

  return { setupTracking, waitForWidgetAndSetupTracking }
}
